import { adminResourcesLanguageEnglish } from '../resources/adminResources';

import type { CallflowTranslationMessages } from './callflowTranslationMessages';

const matchExitCauses = {
  continue_on_max_waiters: 'Continue on max waiters',
  skip_empty: 'Skip empty',
  answered: 'Asnwered',
  exit_key: 'Exit key',
  hangup_on_max_waiters: 'Hangup on max waiters',
  timeout: 'Timeout',
  ...adminResourcesLanguageEnglish.call_sessions.enums.hangup_cause,
};

export const callflowLanguageEnglish: CallflowTranslationMessages = {
  callflows: {
    interface: {
      toggleListVisibility: 'Toggle list visibility',
      toggleMiniMap: 'Toggle mini map',
      noRecordSelected: 'No record selected',
      selectRecord: 'Select a record',
      selectedRecord: 'Selected Record',
      insertElement: 'Insert element',
      rules: 'Rules',
      services: 'Services',
      destinations: 'Destinations',
      selectionModeEnabled: 'Selection mode enabled',
      panningModeEnabled: 'Panning mode enabled',
      copyNode: 'Copy node',
      deleteSelected: 'Delete selected nodes',
      editAndSave: 'Edit and save',
      editAndSaveDescription: 'Edit and save the dialplan settings.',
      description: 'Description',
      backToMainList: 'Back to main list',
      showBackups: 'Show backups',
      backupsForTitle: 'Backups for %{title}',
      backupData: 'Backup data',
      restoreBackup: 'Restore backup',
      saveAsFile: 'Save as file',
      uploadFile: 'Upload file',
      importExport: 'Import/Export',
      importExportDescription: 'Use this interface to export/import your dialplan.',
      controlsTitle: '%{node} controls',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitView: 'Fit view',
      autoPositionNodes: 'Auto position nodes',
      toggleInteractivity: 'Toggle interactivity',
      unsavedExit: 'You have unsaved changes. Are you sure you want to exit?',
      filters: {
        backupFilter: 'Filter backups',
        showAll: 'Show all',
        showOnlyBackups: 'Show only backups',
        hideBackups: 'Hide backups',
      },
      draft: {
        dialogTitle: 'There are unsaved changes',
        dialogText:
          'You have unsaved changes that are newer than the last callflow version. Do you want to apply them?',
        decline: 'Decline',
        apply: 'Apply',
      },
      soundfileUpload: {
        toggle: 'Upload audio file',
        uploading: 'Uploading…',
      },
    },
    components: {
      callPropertyInput: {
        options: {
          called_id_number: 'Called ID Number',
          called_id_name: 'Called ID Name',
          caller_id_number: 'Caller ID Number',
          caller_id_name: 'Caller ID Name',
          remote_network_addr: 'Remote Network Address',
        },
      },
      matchReplaceTestInput: {
        testSubmit: 'Test',
        testMatchLabel: 'Test Match Rules',
        testMatchReplaceLabel: 'Test Match/Replace',
        replacedWith: 'Replaced with %{replacement}',
        testError: 'Validation error: %{error}',
      },
      sortableInstructionsList: {
        instructions: 'Instructions',
        description: 'Description',
        matchExitCauses: 'Match Exit Causes',
        defaultConnection: 'Default Connection',
        presetsLabel: 'Presets',
        presets: {
          busy: 'Busy',
        },
        match_exit_causes: matchExitCauses,
      },
      variableInterpolation: {
        title: 'Variable Interpolation',
        description:
          'You can use variables from the call context in URLs and header values. Warning: referencing an unknown namespace, an undefined variable (without orempty), or an unknown filter will terminate the call.',
        syntax: 'Syntax',
        namespaces: 'Available Namespaces',
        availableVariables: 'Available Variables',
        namespaceCall:
          'account, account_user, account_domain, called_account, caller_id_name, caller_id_number, called_id_name, called_id_number, from_host, to_host, remote_network_addr',
        namespaceCustomVars: 'Custom variables set earlier in the flow (e.g., by a mapping node)',
        namespaceSipHeaders: 'SIP header values (read-only, header names may contain hyphens)',
        namespaceRoot: 'uuid (channel UUID), realm',
        namespaceHelpers: 'date (UTC timestamp as YYYY-MM-DD-HH-MM-SS)',
        filters: 'Filters (chain with |)',
        filterUserPart: 'Part before last @',
        filterDomainPart: 'Part after last @',
        filterUriEncode: 'Percent-encode for safe use in URIs',
        filterOrempty: 'Empty string if undefined (must be first in chain)',
        examples: 'Examples',
      },
    },
    nodes: {
      answer: {
        name: 'Answer',
        delay: 'Delay',
        delayDescription: 'Milliseconds to wait before proceeding, useful to give time to audio path to stabilize',
        answerShouldHaveConnection: 'Answer should have a connection',
      },
      calendar: {
        name: 'Calendar',
        calendarIsNotSelected: 'Calendar is not selected',
        calendarHasNoTarget: 'Calendar has no target',
      },
      call_me: {
        name: 'Call me',
      },
      conference: {
        name: 'Conference',
        conferenceErrorEmpty: 'Conference is not selected',
      },
      disconnect: {
        name: 'Disconnect',
        reason: 'Reason',
        reasonNormal: 'Normal',
        reasonNotFound: 'Not found',
      },
      distribution_list: {
        name: 'Distribution list',
        distributionListErrorEmpty: 'Distribution list is not selected',
        distributionListHasNoTarget: 'Distribution list has no target',
      },
      extension: {
        name: 'Extension',
        connectionShouldHaveMatchExitCause: 'Extension connection should have a match exit cause',
        connectionShouldHaveTarget: 'Extension connection should have a target',
      },
      forward: {
        name: 'Forward',
        number: 'Number',
        forwardErrorEmpty: 'Forward number is not set',
      },
      goto: {
        name: 'Goto',
        gotoErrorEmpty: 'Goto target is not set!',
      },
      groupNode: {
        name: 'Group',
        groupErrorEmpty: 'Group is not selected',
        callTimeout: 'Call Timeout',
      },
      http: {
        name: 'HTTP',
        urlDescription: 'The external URL to call',
        urlErrorEmpty: 'URL cannot be empty',
        urlErrorInvalid: 'URL must be valid and start with http:// or https://',
        method: 'Method',
        methodDescription: 'HTTP method to use for the request',
        methodPost: 'POST',
        methodGet: 'GET',
        headers: 'Headers',
        headersTooltip: `An external HTTP request which must return another Callflow. When callflow execution reaches a CallflowHTTP
        node, a request will be made to the specified URL; if the method is POST, call state will be sent as request
        payload. The contacted server must respond with a callflow, in the form of a JSON object containing entry
        and nodesfields. The returned callflow must not contain loops, i.e. it can't be an arbitrary graph but
        must be a tree; in addition, not all nodes are supported, but only those with the
        x-voismart-simple-callflow-node property. If any of these required conditions for the returned Callflow is
        violated, the call will be dropped.`,
        addHeader: 'Add header',
        headerName: 'Header name',
        headerNameErrorEmpty: 'Header name cannot be empty',
        headerNameErrorInvalid: 'Name must be valid and start with x- or be "authorization"',
        headerValue: 'Header value',
        headerValueErrorEmpty: 'Header value cannot be empty',
        headersError: 'Headers are incomplete',
        sslVerify: 'SSL verify',
      },
      hunt: {
        name: 'Hunt',
        enableScreening: 'Enable screening',
        huntingGroup: 'Hunt group',
        parallel: 'Parallel',
        huntingGroupErrorEmpty: 'Hunt group cannot be empty',
      },
      is_from_queue: {
        name: 'Is from queue?',
        isFromQueueHasNoTarget: '"Is from queue" has no target',
      },
      is_external: {
        name: 'Is external?',
        isExternalHasNoTarget: '"Is external" has no target',
      },
      ivr: {
        name: 'IVR',
        errorSoundfile: 'Error soundfile',
        menuSoundfile: 'Menu soundfile',
        retries: 'Retries',
        selections: 'Selections',
        selection: 'Selection',
        timeout: 'Timeout',
        timeoutSoundfile: 'Timeout soundfile',
        selectionsErrorEmpty: 'IVR selections cannot be empty',
        selectionShouldHaveConnection: 'IVR selections should have a connection',
        soundfileErrorEmpty: 'Soundfile cannot be empty',
      },
      lcr: {
        name: 'LCR',
        userIsNotSelected: 'User is not selected',
      },
      mapping: {
        name: 'Mapping',
        setProperty: 'Set property',
        customSetProperty: 'Custom set property',
        useCustomSetProperty: 'Use custom set property',
        addMappingRule: 'Add match rule',
        replaceRule: 'Replace rule',
        mappingRuleErrorEmpty: 'Match rule cannot be empty',
        mappingHasNoTarget: 'Mapping has no target',
        mapTo: '%{matchRule} to %{replaceRule}',
      },
      playback: {
        name: 'Playback',
        audioSourceNotSelected: 'Audio source is not selected',
        useUrlSource: 'Use URL source',
        useDTMFGathering: 'Use DTMF gathering',
        maxDigits: 'Max digits',
        maxDigitsErrorInvalid: 'Max digits must be a number between 1 and 128',
        outputPropName: 'Output property name',
        outputPropNameErrorEmpty: 'Output property name cannot be empty',
        outputPropNameErrorInvalid: 'Output property name must be valid and start with a letter',
        soundfile: 'Soundfile',
        terminators: 'Terminators',
        terminatorsErrorInvalid: 'Terminators must be valid and contain only digits, # and *',
        timeout: 'Timeout',
        timeoutErrorInvalid: 'Timeout must be a number between 500 and 60000',
      },
      presence_router: {
        name: 'Presence router',
        availableConnection: 'Available connection',
        awayConnection: 'Away connection',
        presenceRouterShouldHaveConnection: 'Presence router should have a connection',
      },
      queue: {
        name: 'Queue',
        queueErrorEmpty: 'Queue is not selected',
        connectionShouldHaveMatchExitCause: 'Queue connection should have a match exit cause',
        connectionShouldHaveTarget: 'Queue connection should have a target',
      },
      response_router: {
        name: 'Response router',
        busyConnection: 'Busy connection',
        timeoutConnection: 'Timeout connection',
        unreachableConnection: 'Unreachable connection',
        responseRouterShouldHaveConnection: 'Response router should have a connection',
      },
      router: {
        name: 'Router',
        matchProperty: 'Match property',
        customMatchProperty: 'Custom match property',
        useCustomMatchProperty: 'Use custom match property',
        callPropertyErrorEmpty: '%{label} cannot be empty',
        callPropertyErrorInvalid:
          '%{label} must be at least 3 symbols, start with a letter and contain only letters, numbers and underscores',
        matchRules: 'Match rules',
        matchRule: 'Match rule',
        matchRuleErrorEmpty: 'Match rule cannot be empty',
        matchRuleShouldHaveConnection: 'Router match rule should have a connection',
      },
      speech: {
        name: 'Speech',
        auth_key: 'Auth key',
        authKeyErrorEmpty: 'Auth key cannot be empty',
        encoding: 'Encoding',
        encodingWav: 'WAV',
        encodingMp3: 'MP3',
        language: 'Language',
        languageIt: 'Italian',
        languageEn: 'English',
        text: 'Text',
        textErrorEmpty: 'Text cannot be empty',
      },
      user: {
        name: 'User',
        userIsNotSelected: 'User is not selected',
        connectionShouldHaveMatchExitCause: 'User connection should have a match exit cause',
        connectionShouldHaveTarget: 'User connection should have a target',
      },
      personal_voicemail: {
        name: 'Voicemail',
      },
      user_voicemail: {
        name: 'Voicemail',
        userIsNotSelected: 'User is not selected',
      },
      ai_assistant: {
        name: 'AI Assistant',
        instructions: 'Instructions',
        instructionsErrorEmpty: 'Instructions cannot be empty',
        model: 'Model',
        voice: 'Voice',
        defaultInstructions:
          "Your job is to answer telephone calls directed to a company. You should welcome the caller and ask how you can help them. You should use your tools to route the call to the most appropriate destination according to the caller's request. You should be polite and professional. You should not allow the user to steer the conversation to topics which are unrelated to the business.",
        transcription: {
          enabled: 'Enable transcription',
          model: 'Transcription model',
        },
      },
      ai_voice_analysis: {
        name: 'AI voice analysis',
        titleStart: 'Start fake voice check',
        titleStop: 'Stop fake voice check',
        action: 'Action',
        actionStart: 'Start analysis',
        actionStop: 'Stop analysis',
        actionDescription:
          'Starts, or stops, the AI analysis of the remote party voice. The Realynx API token must be configured on the domain, otherwise the node has no effect.',
        aiVoiceAnalysisShouldHaveConnection: 'AI voice analysis should have a connection',
      },
      label: 'Label',
    },
    notifications: {
      saved: 'Dialplan saved successfully',
      updated: 'Dialplan updated successfully',
      restored: 'Dialplan backup restored successfully',
      errorSaving: 'Error saving dialplan. Please check your dialplan and try again.',
      callflowDataError: 'Callflow data is not valid: %{error}',
      deleteConfirmation: 'Are you sure you want to delete this dialplan?',
      deleteConfirmationTitle: 'Delete %{title}?',
      nodeIsNotConnectedToMainFlow: 'Node is not connected to main flow',
      errorSorting: 'Error sorting nodes. Please check if the nodes are properly connected and try again.',
      errorSortingMultipleRoots: 'Error sorting nodes. Not all nodes are connected to main flow.',
    },
  },
};
